import {PhoneHQGlyph} from 'ch3-ui-lib';
import { useCallback, useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { useAuthenticatedMedia } from "../../hooks/useAuthenticatedMedia";
import type { MatrixEncryptedFile } from "../../utils/encryptedMedia";
import { claimActiveVideo, releaseActiveVideo } from "../../utils/activeVideoCoordinator";
import { formatVideoDuration, formatVideoSize, getVideoPosterSize } from "../../utils/videoPoster";

interface AuthenticatedVideoProps {
    src: string;
    filename?: string;
    className?: string;
    style?: React.CSSProperties;
    controls?: boolean;
    muted?: boolean;
    loop?: boolean;
    preload?: 'none' | 'metadata' | 'auto';
    poster?: string;
    onLoadedMetadata?: () => void;
    encryptedFile?: MatrixEncryptedFile;
    mimeType?: string;
    thumbnailSrc?: string;
    thumbnailEncryptedFile?: MatrixEncryptedFile;
    thumbnailMimeType?: string;
    width?: number;
    height?: number;
    thumbnailWidth?: number;
    thumbnailHeight?: number;
    durationMs?: number;
    sizeBytes?: number;
}

export const AuthenticatedVideo = ({
                                       src,
                                       filename,
                                       className,
                                       style,
                                       controls = true,
                                       muted = false,
                                       loop = false,
                                       preload = 'metadata',
                                        poster,
                                        onLoadedMetadata,
                                        encryptedFile,
                                        mimeType,
                                        thumbnailSrc,
                                        thumbnailEncryptedFile,
                                        thumbnailMimeType = 'image/jpeg',
                                        width,
                                        height,
                                        thumbnailWidth,
                                        thumbnailHeight,
                                        durationMs,
                                        sizeBytes,
                                    }: AuthenticatedVideoProps) => {
    const [activated, setActivated] = useState(false);
    const [playbackFailed, setPlaybackFailed] = useState(false);
    const instanceId = useRef(Symbol('authenticated-video')).current;
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const onLoadedMetadataRef = useRef(onLoadedMetadata);
    onLoadedMetadataRef.current = onLoadedMetadata;
    const { blobUrl, error, retry } = useAuthenticatedMedia(
        activated ? src : '',
        activated ? encryptedFile : undefined,
        mimeType,
        'bulk'
    );
    const { blobUrl: thumbnailBlobUrl, error: thumbnailError } = useAuthenticatedMedia(
        thumbnailSrc ?? '',
        thumbnailEncryptedFile,
        thumbnailMimeType
    );

    const posterSize = getVideoPosterSize(width ?? thumbnailWidth, height ?? thumbnailHeight);
    const hasKnownSize = Boolean((width ?? thumbnailWidth) && (height ?? thumbnailHeight));
    const reportedPosterLayout = useRef(false);

    const reportPosterLayout = useCallback(() => {
        if (reportedPosterLayout.current) return;
        reportedPosterLayout.current = true;
        onLoadedMetadata?.();
    }, [onLoadedMetadata]);

    useEffect(() => {
        if (hasKnownSize || !thumbnailSrc || thumbnailError) {
            reportPosterLayout();
        }
    }, [hasKnownSize, thumbnailSrc, thumbnailError, reportPosterLayout]);

    const teardownVideo = useCallback(() => {
        const element = videoRef.current;
        if (!element) return;
        videoRef.current = null;
        element.pause();
        element.removeAttribute('src');
        element.load();
        element.remove();
    }, []);

    const ensureVideoElement = useCallback((): HTMLVideoElement | null => {
        if (videoRef.current) return videoRef.current;
        const container = containerRef.current;
        if (!container) return null;

        const element = document.createElement('video');
        element.controls = controls;
        element.setAttribute('controlsList', 'nodownload');
        element.muted = muted;
        element.loop = loop;
        element.preload = preload;
        element.playsInline = true;
        element.style.width = '100%';
        element.style.height = '100%';
        element.style.objectFit = 'contain';
        element.style.display = 'none';
        element.addEventListener('loadedmetadata', () => onLoadedMetadataRef.current?.());
        element.addEventListener('error', () => setPlaybackFailed(true));
        container.appendChild(element);
        videoRef.current = element;
        return element;
    }, [controls, muted, loop, preload]);

    useEffect(() => {
        return () => {
            releaseActiveVideo(instanceId);
            teardownVideo();
        };
    }, [instanceId, teardownVideo]);

    const isPlayerVisible = activated && Boolean(blobUrl) && !playbackFailed;

    useEffect(() => {
        if (!activated || playbackFailed) {
            teardownVideo();
            return;
        }
        const element = videoRef.current;
        if (!element) return;

        element.poster = poster ?? thumbnailBlobUrl;
        if (!blobUrl) return;

        element.style.display = 'block';
        if (element.getAttribute('src') !== blobUrl) {
            element.src = blobUrl;
            const attempt = element.play();
            attempt?.catch?.(() => undefined);
        }
    }, [activated, playbackFailed, blobUrl, poster, thumbnailBlobUrl, teardownVideo]);

    const handlePlay = useCallback(() => {
        claimActiveVideo(instanceId, () => {
            setActivated(false);
            setPlaybackFailed(false);
        });
        if (error) {
            retry();
        }
        setPlaybackFailed(false);
        setActivated(true);
        const element = ensureVideoElement();
        const attempt = element?.play();
        attempt?.catch?.(() => undefined);
    }, [instanceId, error, retry, ensureVideoElement]);

    const handleDownload = useCallback(() => {
        if (!blobUrl) return;

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || 'video';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [blobUrl, filename]);

    const failed = Boolean(error) || playbackFailed;
    const showSpinner = activated && !failed && !blobUrl;
    const duration = formatVideoDuration(durationMs);
    const sizeLabel = formatVideoSize(sizeBytes);
    const details = [duration, sizeLabel].filter(Boolean).join(' · ');

    return (
        <div
            className={`relative group overflow-hidden rounded-lg bg-line ${className ?? ''}`}
            style={{ ...style, width: posterSize.width, height: posterSize.height, maxWidth: '100%' }}
        >
            <div ref={containerRef} className="absolute inset-0" />
            {!isPlayerVisible && (
                <button
                    type="button"
                    data-testid="chat.video.poster"
                    onClick={handlePlay}
                    disabled={showSpinner || playbackFailed}
                    aria-label={error ? "Retry video" : "Play video"}
                    className="absolute inset-0 block overflow-hidden"
                >
                    {thumbnailBlobUrl && (
                        <img
                            src={thumbnailBlobUrl}
                            alt={filename || 'Video'}
                            className="absolute inset-0 h-full w-full object-cover"
                            onLoad={reportPosterLayout}
                            onError={reportPosterLayout}
                        />
                    )}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-scrim text-white">
                        {showSpinner ? (
                            <PhoneHQGlyph name="refresh" size={36} className="animate-spin"/>
                        ) : (
                            <PhoneHQGlyph name={failed ? 'videocam_off' : 'play_circle_filled'} size={48}/>
                        )}
                        {!thumbnailBlobUrl && filename && (
                            <span className="mt-1 max-w-full truncate px-2 text-xs">{filename}</span>
                        )}
                    </div>
                    {(details || failed) && (
                        <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-xs text-white">
                            {failed ? 'Failed to load video' : details}
                        </span>
                    )}
                </button>
            )}
            {playbackFailed && blobUrl && (
                <a
                    href={blobUrl}
                    download={filename || 'video'}
                    data-testid="chat.video.download"
                    className="absolute bottom-1 left-1 flex items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs text-white hover:bg-black/90"
                >
                    <Download color={'white'} size={14} />
                    Download video
                </a>
            )}
            {isPlayerVisible && (
                <button
                    onClick={handleDownload}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                    aria-label="Download video"
                >
                    <Download className="p-1.5" color={'white'} size={32} />
                </button>
            )}
        </div>
    );
};
